import { MetaConversation } from 'lib-shared-legacy-types';
import { useEffect, useMemo } from 'react';

import { useLazyCustomFields } from 'utils/customFields/useLazyCustomFields';

import {
  buildElevionConversationContext,
  ElevionConversationContext,
} from '../../utils/conditions';

/**
 * Builds the {@link ElevionConversationContext} used to evaluate card render
 * conditions in the flat `sidebar-cards-admin-management` model.
 *
 * When `enabled` is `false` (no card declares conditions) the custom-field
 * queries are skipped and only queue/tags are populated, keeping the cost zero
 * for orgs that don't use conditions.
 */
export const useSidebarConditionContext = (
  metaConversation: MetaConversation,
  enabled: boolean,
): ElevionConversationContext => {
  const conversationId = metaConversation.conversation?.id;
  const requesterId = metaConversation.requester?.id;

  const [
    readConversationCustomFields,
    { customFields: conversationCustomFields },
  ] = useLazyCustomFields();
  const [readContactCustomFields, { customFields: contactCustomFields }] =
    useLazyCustomFields();

  useEffect(() => {
    if (enabled && conversationId) {
      readConversationCustomFields({
        entityId: 'conversation',
        entityInstanceId: `${conversationId}`,
      });
    }
  }, [enabled, conversationId, readConversationCustomFields]);

  useEffect(() => {
    if (enabled && requesterId) {
      readContactCustomFields({
        entityId: 'contact',
        entityInstanceId: requesterId,
      });
    }
  }, [enabled, requesterId, readContactCustomFields]);

  return useMemo(
    () =>
      buildElevionConversationContext({
        queueId: metaConversation.conversation?.queue?.id ?? null,
        tags: metaConversation.conversation?.tags ?? null,
        conversationCustomFields:
          enabled && conversationId ? conversationCustomFields : [],
        contactCustomFields: enabled && requesterId ? contactCustomFields : [],
      }),
    [
      metaConversation.conversation?.queue?.id,
      metaConversation.conversation?.tags,
      conversationCustomFields,
      contactCustomFields,
      enabled,
      conversationId,
      requesterId,
    ],
  );
};
